import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { mailSettingsAPI } from '../services/api';
import './Users.css';

const MailSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [smtp, setSmtp] = useState({
    mail_mailer: 'smtp',
    mail_smtp_host: '',
    mail_smtp_port: 587,
    mail_smtp_username: '',
    mail_smtp_encryption: '',
    mail_from_address: '',
    mail_from_name: '',
    smtp_password_set: false,
  });
  const [smtpPassword, setSmtpPassword] = useState('');
  const [registrationSubject, setRegistrationSubject] = useState('');
  const [registrationBodyHtml, setRegistrationBodyHtml] = useState('');
  const [orderSubject, setOrderSubject] = useState('');
  const [orderBodyHtml, setOrderBodyHtml] = useState('');
  const [passwordResetSubject, setPasswordResetSubject] = useState('');
  const [passwordResetBodyHtml, setPasswordResetBodyHtml] = useState('');
  const [placeholders, setPlaceholders] = useState({
    registration: [],
    order_confirmed: [],
    password_reset: [],
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await mailSettingsAPI.get();
      const s = data.smtp || {};
      setSmtp({
        mail_mailer: s.mail_mailer || 'smtp',
        mail_smtp_host: s.mail_smtp_host || '',
        mail_smtp_port: s.mail_smtp_port || 587,
        mail_smtp_username: s.mail_smtp_username || '',
        mail_smtp_encryption: s.mail_smtp_encryption || '',
        mail_from_address: s.mail_from_address || '',
        mail_from_name: s.mail_from_name || '',
        smtp_password_set: !!s.smtp_password_set,
      });
      setRegistrationSubject(data.templates?.registration?.subject || '');
      setRegistrationBodyHtml(data.templates?.registration?.body_html || '');
      setOrderSubject(data.templates?.order_confirmed?.subject || '');
      setOrderBodyHtml(data.templates?.order_confirmed?.body_html || '');
      setPasswordResetSubject(data.templates?.password_reset?.subject || '');
      setPasswordResetBodyHtml(data.templates?.password_reset?.body_html || '');
      setPlaceholders({
        registration: data.templates?.placeholders?.registration || [],
        order_confirmed: data.templates?.placeholders?.order_confirmed || [],
        password_reset: data.templates?.placeholders?.password_reset || [],
      });
      setSmtpPassword('');
    } catch (error) {
      toast.error('Could not load mail settings: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e, extra = {}) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {
        mail_mailer: smtp.mail_mailer,
        mail_smtp_host: smtp.mail_smtp_host,
        mail_smtp_port: parseInt(String(smtp.mail_smtp_port), 10) || 587,
        mail_smtp_username: smtp.mail_smtp_username,
        mail_smtp_encryption: smtp.mail_smtp_encryption || null,
        mail_from_address: smtp.mail_from_address || null,
        mail_from_name: smtp.mail_from_name || null,
        registration_subject: registrationSubject,
        registration_body_html: registrationBodyHtml,
        order_confirmed_subject: orderSubject,
        order_confirmed_body_html: orderBodyHtml,
        password_reset_subject: passwordResetSubject,
        password_reset_body_html: passwordResetBodyHtml,
        clear_registration_template: !!extra.clear_registration_template,
        clear_order_template: !!extra.clear_order_template,
        clear_password_reset_template: !!extra.clear_password_reset_template,
        clear_smtp_password: !!extra.clear_smtp_password,
      };
      if (smtpPassword.trim()) {
        payload.mail_smtp_password = smtpPassword.trim();
      }
      const { data } = await mailSettingsAPI.update(payload);
      toast.success('Mail settings saved');
      const s = data.smtp || {};
      setSmtp((prev) => ({
        ...prev,
        smtp_password_set: !!s.smtp_password_set,
      }));
      setSmtpPassword('');
      setRegistrationSubject(data.templates?.registration?.subject || '');
      setRegistrationBodyHtml(data.templates?.registration?.body_html || '');
      setOrderSubject(data.templates?.order_confirmed?.subject || '');
      setOrderBodyHtml(data.templates?.order_confirmed?.body_html || '');
      setPasswordResetSubject(data.templates?.password_reset?.subject || '');
      setPasswordResetBodyHtml(data.templates?.password_reset?.body_html || '');
    } catch (error) {
      toast.error('Save failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading mail settings…</div>;

  const phReg = placeholders.registration;
  const phOrder = placeholders.order_confirmed;
  const phPasswordReset = placeholders.password_reset;

  return (
    <div className="users-container mail-settings-page">
      <form onSubmit={(e) => submit(e, {})}>
        <div className="table-container" style={{ padding: 22, maxWidth: 920, marginBottom: 20 }}>
          <h3 style={{ marginBottom: 8 }}>SMTP & sender</h3>
          <p style={{ marginBottom: 18, color: '#666', fontSize: 14, lineHeight: 1.5 }}>
            When <strong>SMTP host</strong> is set here, the API uses these values for transactional mail (registration
            welcome, order confirmation, password reset). Leave host empty to rely on server <code>.env</code> mail configuration only.
          </p>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group">
              <label>Mailer</label>
              <select
                value={smtp.mail_mailer}
                onChange={(e) => setSmtp((s) => ({ ...s, mail_mailer: e.target.value }))}
              >
                <option value="smtp">smtp</option>
                <option value="log">log (debug)</option>
              </select>
            </div>
            <div className="form-group">
              <label>SMTP port</label>
              <input
                type="number"
                min={1}
                max={65535}
                value={smtp.mail_smtp_port}
                onChange={(e) => setSmtp((s) => ({ ...s, mail_smtp_port: e.target.value }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label>SMTP host</label>
            <input
              type="text"
              placeholder="e.g. smtp.mailtrap.io"
              value={smtp.mail_smtp_host}
              onChange={(e) => setSmtp((s) => ({ ...s, mail_smtp_host: e.target.value }))}
            />
          </div>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group">
              <label>SMTP username</label>
              <input
                type="text"
                autoComplete="off"
                value={smtp.mail_smtp_username}
                onChange={(e) => setSmtp((s) => ({ ...s, mail_smtp_username: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>SMTP password {smtp.smtp_password_set ? <span style={{ color: '#059669' }}>(saved)</span> : null}</label>
              <input
                type="password"
                autoComplete="new-password"
                placeholder={smtp.smtp_password_set ? 'Enter new password to replace' : ''}
                value={smtpPassword}
                onChange={(e) => setSmtpPassword(e.target.value)}
              />
            </div>
          </div>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group">
              <label>Encryption</label>
              <select
                value={smtp.mail_smtp_encryption || ''}
                onChange={(e) => setSmtp((s) => ({ ...s, mail_smtp_encryption: e.target.value }))}
              >
                <option value="">(none)</option>
                <option value="tls">tls</option>
                <option value="ssl">ssl</option>
              </select>
            </div>
            <div className="form-group">
              <label>From name</label>
              <input
                type="text"
                value={smtp.mail_from_name}
                onChange={(e) => setSmtp((s) => ({ ...s, mail_from_name: e.target.value }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label>From email</label>
            <input
              type="email"
              value={smtp.mail_from_address}
              onChange={(e) => setSmtp((s) => ({ ...s, mail_from_address: e.target.value }))}
            />
          </div>
          <div className="form-actions" style={{ gap: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save mail settings'}
            </button>
            {smtp.smtp_password_set ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={saving}
                onClick={(e) => submit(e, { clear_smtp_password: true })}
              >
                Remove saved SMTP password
              </button>
            ) : null}
          </div>
        </div>

        <div className="table-container" style={{ padding: 22, maxWidth: 920, marginBottom: 20 }}>
          <h3 style={{ marginBottom: 8 }}>Registration email</h3>
          <p style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
            Leave subject and body empty to use built-in templates. Placeholders in subject/body:{' '}
            <code>{(phReg || []).map((p) => `{{${p}}}`).join(', ') || '—'}</code>
          </p>
          <div className="form-group">
            <label>Subject (optional)</label>
            <input
              type="text"
              placeholder="Default: Welcome to {app name}"
              value={registrationSubject}
              onChange={(e) => setRegistrationSubject(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>HTML body (optional)</label>
            <textarea
              rows={10}
              style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
              placeholder="Custom HTML inner content; wrapped in branded shell. Use placeholders above."
              value={registrationBodyHtml}
              onChange={(e) => setRegistrationBodyHtml(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={saving}
            onClick={(e) => submit(e, { clear_registration_template: true })}
          >
            Reset registration template to default
          </button>
        </div>

        <div className="table-container" style={{ padding: 22, maxWidth: 920, marginBottom: 20 }}>
          <h3 style={{ marginBottom: 8 }}>Order confirmation email</h3>
          <p style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
            Placeholders: <code>{(phOrder || []).map((p) => `{{${p}}}`).join(', ') || '—'}</code>.{' '}
            <code>{'{{shipping_html}}'}</code> is safe HTML for the shipping block.
          </p>
          <div className="form-group">
            <label>Subject (optional)</label>
            <input
              type="text"
              placeholder="Default: Order confirmed — {order number}"
              value={orderSubject}
              onChange={(e) => setOrderSubject(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>HTML body (optional)</label>
            <textarea
              rows={12}
              style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
              placeholder="Custom HTML inner content; wrapped in branded shell."
              value={orderBodyHtml}
              onChange={(e) => setOrderBodyHtml(e.target.value)}
            />
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={saving}
              onClick={(e) => submit(e, { clear_order_template: true })}
            >
              Reset order template to default
            </button>
          </div>
        </div>

        <div className="table-container" style={{ padding: 22, maxWidth: 920 }}>
          <h3 style={{ marginBottom: 8 }}>Password reset email</h3>
          <p style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
            Sent when a user requests a password reset via email. Placeholders:{' '}
            <code>{(phPasswordReset || []).map((p) => `{{${p}}}`).join(', ') || '—'}</code>.{' '}
            <code>{'{{otp_code}}'}</code> is the 6-digit code the user enters in the app.
          </p>
          <div className="form-group">
            <label>Subject (optional)</label>
            <input
              type="text"
              placeholder="Default: Reset your password"
              value={passwordResetSubject}
              onChange={(e) => setPasswordResetSubject(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>HTML body (optional)</label>
            <textarea
              rows={12}
              style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
              placeholder="Custom HTML inner content; wrapped in branded shell."
              value={passwordResetBodyHtml}
              onChange={(e) => setPasswordResetBodyHtml(e.target.value)}
            />
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={saving}
              onClick={(e) => submit(e, { clear_password_reset_template: true })}
            >
              Reset password reset template to default
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MailSettings;
